﻿const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

class MelaxinVideoCarousel extends HTMLElement {
  connectedCallback() {
    if (this.dataset.ready === 'true') return;
    this.dataset.ready = 'true';
    this.track = this.querySelector('[data-carousel-track]');
    this.dialog = this.querySelector('[data-reel]');
    this.reelTrack = this.querySelector('[data-reel-track]');
    this.realReelItems = [...this.querySelectorAll('[data-reel-item]')];
    this.currentIndex = 0;
    this.muted = true;

    if (!this.track || !this.dialog || !this.reelTrack) return;

    this.buildLoop();
    this.buildReelLoop();
    this.prewarmPreviewVideos(0);
    this.observePreviewVideos();
    this.bindControls();
    this.setupAutoScroll();
    requestAnimationFrame(() => {
      this.measureLoop();
      this.scheduleAutoScroll();
    });
  }

  buildReelLoop() {
    if (this.realReelItems.length > 1) {
      const headClone = this.realReelItems.at(-1).cloneNode(true);
      const tailClone = this.realReelItems[0].cloneNode(true);
      [headClone, tailClone].forEach((clone) => {
        clone.dataset.reelClone = 'true';
        clone.setAttribute('aria-hidden', 'true');
      });
      this.reelTrack.prepend(headClone);
      this.reelTrack.append(tailClone);
    }
    this.reelItems = [...this.reelTrack.querySelectorAll('[data-reel-item]')];
    this.reelVideos = this.reelItems.map((item) => item.querySelector('video'));
  }

  buildLoop() {
    this.realCarouselSlides = [...this.track.querySelectorAll('[data-carousel-slide]')];
    if (this.realCarouselSlides.length > 1) {
      this.realCarouselSlides
        .slice()
        .reverse()
        .forEach((slide) => {
          const clone = slide.cloneNode(true);
          clone.dataset.carouselClone = 'true';
          clone.setAttribute('aria-hidden', 'true');
          this.track.prepend(clone);
        });

      const trailingClones = this.realCarouselSlides.map((slide) => {
        const clone = slide.cloneNode(true);
        clone.dataset.carouselClone = 'true';
        clone.setAttribute('aria-hidden', 'true');
        this.track.append(clone);
        return clone;
      });

      this.firstRealSlide = this.realCarouselSlides[0];
      this.firstTailClone = trailingClones[0];
    }
    this.carouselControls = [...this.querySelectorAll('[data-carousel-control]')];
    this.carouselPrevious = this.querySelector('[data-carousel-prev]');
    this.carouselNext = this.querySelector('[data-carousel-next]');
    this.updateCarouselControls();
  }

  getCarouselMaxIndex() {
    return Math.max(0, this.realCarouselSlides.length - 1);
  }

  getCarouselIndex() {
    const firstSlide = this.realCarouselSlides[0];
    const secondSlide = this.realCarouselSlides[1];
    if (!firstSlide || !secondSlide) return 0;

    const step = secondSlide.offsetLeft - firstSlide.offsetLeft;
    if (!step) return 0;

    const start = this.realStart ?? 0;
    const slideCount = this.realCarouselSlides.length;
    const rawIndex = Math.round((this.track.scrollLeft - start) / step);
    return ((rawIndex % slideCount) + slideCount) % slideCount;
  }

  prewarmPreviewVideos(centerIndex = 0) {
    const videos = this.realCarouselSlides
      ?.slice(Math.max(0, centerIndex - 1), Math.min(this.realCarouselSlides.length, centerIndex + 3))
      .map((slide) => slide.querySelector('video'))
      .filter(Boolean) || [];

    videos.forEach((video) => {
      if (video.readyState >= HTMLMediaElement.HAVE_FUTURE_DATA || video.preload === 'auto') return;
      video.preload = 'auto';
      video.load();
    });
  }

  updateCarouselControls() {
    const index = this.pendingCarouselIndex ?? this.getCarouselIndex();
    const maxIndex = this.getCarouselMaxIndex();

    this.setCarouselControl(index);
    if (this.carouselPrevious) this.carouselPrevious.disabled = this.realCarouselSlides.length < 2;
    if (this.carouselNext) this.carouselNext.disabled = this.realCarouselSlides.length < 2;
  }

  setCarouselControl(index) {
    this.carouselControls?.forEach((control, controlIndex) => {
      const active = controlIndex === index;
      control.classList.toggle('is-active', active);
      control.setAttribute('aria-selected', String(active));
    });
  }

  scrollCarousel(index) {
    const firstSlide = this.realCarouselSlides[0];
    const targetSlide = this.realCarouselSlides[Math.min(this.getCarouselMaxIndex(), Math.max(0, index))];
    if (!firstSlide || !targetSlide) return;

    const targetIndex = Math.min(this.getCarouselMaxIndex(), Math.max(0, index));
    const currentIndex = this.getCarouselIndex();
    const lastIndex = this.getCarouselMaxIndex();
    const step = this.realCarouselSlides[1]?.offsetLeft - firstSlide.offsetLeft;
    let targetLeft = (this.realStart ?? 0) + targetSlide.offsetLeft - firstSlide.offsetLeft;

    if (!this.setWidth) this.measureLoop();
    const isLoopBoundary = this.setWidth &&
      ((currentIndex === lastIndex && targetIndex === 0) ||
        (currentIndex === 0 && targetIndex === lastIndex));
    if (isLoopBoundary) {
      this.pendingCarouselIndex = targetIndex;
      targetLeft = targetIndex === 0
        ? (this.realStart ?? 0) + this.setWidth
        : (this.realStart ?? 0) - step;
      clearTimeout(this.carouselScrollTimer);
      this.setCarouselControl(targetIndex);
    }

    this.track.scrollTo({
      left: targetLeft,
      behavior: reducedMotion.matches ? 'auto' : 'smooth',
    });
  }

  stepCarousel(direction) {
    const firstSlide = this.realCarouselSlides[0];
    const secondSlide = this.realCarouselSlides[1];
    if (!firstSlide || !secondSlide) return;
    const step = secondSlide.offsetLeft - firstSlide.offsetLeft;
    if (!step) return;
    this.track.scrollBy({
      left: step * direction,
      behavior: reducedMotion.matches ? 'auto' : 'smooth',
    });
  }

  measureLoop() {
    if (!this.firstRealSlide || !this.firstTailClone) return;
    const trackStyles = getComputedStyle(this.track);
    const inlinePadding = parseFloat(trackStyles.paddingInlineStart) || 0;
    const trackRect = this.track.getBoundingClientRect();
    const slideRect = this.firstRealSlide.getBoundingClientRect();
    this.realStart = this.track.scrollLeft + slideRect.left - trackRect.left - inlinePadding;
    this.setWidth = this.firstTailClone.offsetLeft - this.firstRealSlide.offsetLeft;
    if (!this.hasPositioned && this.setWidth) {
      this.track.scrollLeft = this.realStart;
      this.hasPositioned = true;
    }
  }

  onCarouselScroll() {
    // Let the browser finish the one-card snap before correcting a loop
    // boundary. Normalizing during the animated scroll can interrupt the
    // last-to-first transition and leave the next swipe one step behind.
    this.updateCarouselControls();
    clearTimeout(this.carouselScrollTimer);
    this.carouselScrollTimer = setTimeout(() => this.finishCarouselScroll(), 80);
  }

  normalizeCarouselPosition() {
    if (!this.setWidth) return;

    const position = this.track.scrollLeft;
    let target = position;
    while (target < this.realStart - 1) target += this.setWidth;
    while (target >= this.realStart + this.setWidth) target -= this.setWidth;
    if (target === position) return;

    this.pendingCarouselIndex = null;
    this.jumpWithoutSnap(this.track, 'scrollLeft', target);
  }

  finishCarouselScroll() {
    if (!this.setWidth) return;
    const position = this.track.scrollLeft;
    let target = position;
    while (target < this.realStart - 1) target += this.setWidth;
    while (target >= this.realStart + this.setWidth) target -= this.setWidth;
    if (target === position) return;
    this.pendingCarouselIndex = null;
    this.jumpWithoutSnap(this.track, 'scrollLeft', target);
    this.updateCarouselControls();
  }

  setupAutoScroll() {
    this.autoScrollEnabled = false;
    this.autoScrollDelay = Math.max(2, Number(this.dataset.autoScrollInterval) || 4) * 1000;
    this.pauseAutoScroll = this.dataset.pauseAutoScroll !== 'false';
    this.autoScrollVisible = true;
    if (!this.autoScrollEnabled || reducedMotion.matches) return;

    this.pauseCarousel = () => {
      if (!this.pauseAutoScroll) return;
      this.autoScrollPaused = true;
      clearTimeout(this.autoScrollTimer);
    };
    this.resumeCarousel = () => {
      this.autoScrollPaused = false;
      this.scheduleAutoScroll();
    };
    this.handleVisibility = () => {
      if (document.hidden) clearTimeout(this.autoScrollTimer);
      else this.scheduleAutoScroll();
    };

    this.track.addEventListener('pointerdown', this.pauseCarousel, { passive: true });
    this.track.addEventListener('pointerup', this.resumeCarousel, { passive: true });
    this.track.addEventListener('pointercancel', this.resumeCarousel, { passive: true });
    this.track.addEventListener('mouseenter', this.pauseCarousel);
    this.track.addEventListener('mouseleave', this.resumeCarousel);
    this.track.addEventListener('focusin', this.pauseCarousel);
    this.track.addEventListener('focusout', this.resumeCarousel);
    document.addEventListener('visibilitychange', this.handleVisibility);

    if ('IntersectionObserver' in window) {
      this.autoScrollObserver = new IntersectionObserver(([entry]) => {
        this.autoScrollVisible = entry?.isIntersecting ?? true;
        if (this.autoScrollVisible) this.scheduleAutoScroll();
        else clearTimeout(this.autoScrollTimer);
      }, { threshold: 0.2 });
      this.autoScrollObserver.observe(this);
    }
  }

  scheduleAutoScroll() {
    clearTimeout(this.autoScrollTimer);
    if (!this.autoScrollEnabled || reducedMotion.matches || this.autoScrollPaused || !this.autoScrollVisible || document.hidden || !this.dialog?.hidden) return;
    this.autoScrollTimer = setTimeout(() => {
      const firstSlide = this.realCarouselSlides?.[0];
      if (!firstSlide) return;
      const gap = parseFloat(getComputedStyle(this.track).columnGap || getComputedStyle(this.track).gap) || 0;
      this.track.scrollBy({ left: firstSlide.offsetWidth + gap, behavior: 'smooth' });
      this.scheduleAutoScroll();
    }, this.autoScrollDelay);
  }

  observePreviewVideos() {
    if (!('IntersectionObserver' in window)) return;
    this.previewObserver = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          const video = entry.target;
          if (entry.isIntersecting && !reducedMotion.matches) {
            const slide = video.closest('[data-carousel-slide]');
            const slideIndex = slide ? this.realCarouselSlides.indexOf(slide) : -1;
            if (slideIndex >= 0) this.prewarmPreviewVideos(slideIndex);
            video.muted = true;
            video.play().catch(() => {});
          } else {
            video.pause();
          }
        });
      },
      { root: this.track, rootMargin: '0px', threshold: 0.2 }
    );
    this.track.querySelectorAll('video').forEach((video) => this.previewObserver.observe(video));
  }

  bindControls() {
    this.carouselPrevious?.addEventListener('click', () => this.stepCarousel(-1));
    this.carouselNext?.addEventListener('click', () => this.stepCarousel(1));
    this.carouselControls?.forEach((control) => {
      control.addEventListener('click', () => this.scrollCarousel(Number(control.dataset.carouselControl) || 0));
    });
    this.track.addEventListener('scroll', () => this.onCarouselScroll(), { passive: true });
    this.track.addEventListener('scrollend', () => {
      clearTimeout(this.carouselScrollTimer);
      this.finishCarouselScroll();
    }, { passive: true });
    this.track.addEventListener('click', (event) => {
      const opener = event.target.closest('[data-reel-open]');
      if (opener) this.open(Number(opener.dataset.reelOpen) || 0, opener);
    });
    this.querySelector('[data-reel-close]')?.addEventListener('click', () => this.close());
    this.querySelector('[data-reel-share]')?.addEventListener('click', () => this.share());
    this.querySelector('[data-reel-mute]')?.addEventListener('click', () => this.setMuted(!this.muted));
    this.querySelector('[data-reel-prev]')?.addEventListener('click', () => this.step(-1));
    this.querySelector('[data-reel-next]')?.addEventListener('click', () => this.step(1));
    this.reelTrack.addEventListener('scroll', () => this.onReelScroll(), { passive: true });
    this.reelItems.forEach((item, index) => {
      item.addEventListener('click', (event) => {
        if (event.target.closest('button')) return;
        const video = this.reelVideos[index];
        if (!video) return;
        if (video.paused) video.play().catch(() => {});
        else video.pause();
      });
    });
    this.handleKeydown = (event) => {
      if (this.dialog.hidden) return;
      if (event.key === 'Escape') this.close();
      if (event.key === 'ArrowDown' || event.key === 'ArrowRight') {
        event.preventDefault();
        this.step(1);
      }
      if (event.key === 'ArrowUp' || event.key === 'ArrowLeft') {
        event.preventDefault();
        this.step(-1);
      }
      if (event.key === 'Tab') this.trapFocus(event);
    };
  }

  onReelScroll() {
    if (this.reelFrame) return;
    this.reelFrame = requestAnimationFrame(() => {
      this.reelFrame = null;
      const closest = this.reelItems.reduce((best, item, index) => {
        const distance = Math.abs(item.offsetTop - this.reelTrack.scrollTop);
        return distance < best.distance ? { index, distance } : best;
      }, { index: 0, distance: Infinity });
      this.setCurrent(closest.index);
      clearTimeout(this.reelScrollTimer);
      this.reelScrollTimer = setTimeout(() => this.finishReelScroll(), 100);
    });
  }

  finishReelScroll() {
    if (this.realReelItems.length < 2) return;
    const lastDomIndex = this.reelItems.length - 1;
    if (this.currentDomIndex === 0) this.teleportReel(0, lastDomIndex - 1);
    if (this.currentDomIndex === lastDomIndex) this.teleportReel(lastDomIndex, 1);
  }

  teleportReel(fromIndex, toIndex) {
    const source = this.reelVideos[fromIndex];
    const target = this.reelVideos[toIndex];
    if (source && target && Number.isFinite(source.currentTime)) target.currentTime = source.currentTime;
    source?.pause();
    this.jumpWithoutSnap(this.reelTrack, 'scrollTop', this.reelItems[toIndex].offsetTop);
    this.setCurrent(toIndex);
  }

  jumpWithoutSnap(element, property, value) {
    element.classList.add('is-loop-jumping');
    element[property] = value;
    requestAnimationFrame(() => requestAnimationFrame(() => element.classList.remove('is-loop-jumping')));
  }

  open(index, opener) {
    clearTimeout(this.autoScrollTimer);
    this.lastFocused = opener;
    this.dialog.hidden = false;
    this.dialog.setAttribute('aria-hidden', 'false');
    document.documentElement.classList.add('melaxin-reel-open');
    document.addEventListener('keydown', this.handleKeydown);
    const domIndex = this.realReelItems.length > 1 ? index + 1 : index;
    this.reelTrack.scrollTop = this.reelItems[domIndex]?.offsetTop || 0;
    this.currentDomIndex = -1;
    this.currentIndex = -1;
    this.setMuted(false);
    this.setCurrent(domIndex);
    requestAnimationFrame(() => this.querySelector('[data-reel-close]')?.focus());
  }

  close() {
    this.reelVideos.forEach((video) => video?.pause());
    this.dialog.hidden = true;
    this.dialog.setAttribute('aria-hidden', 'true');
    document.documentElement.classList.remove('melaxin-reel-open');
    document.removeEventListener('keydown', this.handleKeydown);
    this.lastFocused?.focus();
    this.scheduleAutoScroll();
  }

  setCurrent(domIndex) {
    if (domIndex < 0 || domIndex >= this.reelItems.length || domIndex === this.currentDomIndex) return;
    const previous = this.reelVideos[this.currentDomIndex];
    previous?.pause();
    previous?.removeEventListener('timeupdate', this.updateProgress);
    this.currentDomIndex = domIndex;
    this.currentIndex = Number(this.reelItems[domIndex].dataset.reelItem) || 0;
    const video = this.reelVideos[domIndex];
    this.progress = this.querySelector('[data-reel-progress]');
    if (this.progress) this.progress.style.transform = 'scaleX(0)';
    this.updateProgress = () => {
      const ratio = video?.duration ? video.currentTime / video.duration : 0;
      if (this.progress) this.progress.style.transform = `scaleX(${Math.min(1, Math.max(0, ratio))})`;
    };
    if (video) {
      video.muted = this.muted;
      video.addEventListener('timeupdate', this.updateProgress);
      video.play().catch(() => {
        this.setMuted(true);
        video.play().catch(() => {});
      });
    }
    const previousButton = this.querySelector('[data-reel-prev]');
    const nextButton = this.querySelector('[data-reel-next]');
    if (previousButton) previousButton.disabled = this.realReelItems.length < 2;
    if (nextButton) nextButton.disabled = this.realReelItems.length < 2;
  }

  step(direction) {
    const target = this.reelItems[this.currentDomIndex + direction];
    if (!target) return;
    this.reelTrack.scrollTo({ top: target.offsetTop, behavior: reducedMotion.matches ? 'auto' : 'smooth' });
  }

  setMuted(muted) {
    this.muted = muted;
    this.reelVideos.forEach((video) => {
      if (video) video.muted = muted;
    });
    this.dialog.classList.toggle('is-unmuted', !muted);
    this.querySelector('[data-reel-mute]')?.setAttribute('aria-pressed', String(muted));
  }

  async share() {
    if (navigator.share) {
      try { await navigator.share({ title: document.title, url: window.location.href }); } catch (_) {}
      return;
    }
    try {
      await navigator.clipboard.writeText(window.location.href);
      const button = this.querySelector('[data-reel-share]');
      button?.classList.add('is-copied');
      setTimeout(() => button?.classList.remove('is-copied'), 1600);
    } catch (_) {}
  }

  trapFocus(event) {
    const controls = [...this.dialog.querySelectorAll('button:not(:disabled)')];
    if (!controls.length) return;
    const first = controls[0];
    const last = controls[controls.length - 1];
    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  }

  disconnectedCallback() {
    this.previewObserver?.disconnect();
    clearTimeout(this.carouselScrollTimer);
    clearTimeout(this.reelScrollTimer);
    clearTimeout(this.autoScrollTimer);
    this.autoScrollObserver?.disconnect();
    if (this.pauseCarousel) {
      this.track?.removeEventListener('pointerdown', this.pauseCarousel);
      this.track?.removeEventListener('pointerup', this.resumeCarousel);
      this.track?.removeEventListener('pointercancel', this.resumeCarousel);
      this.track?.removeEventListener('mouseenter', this.pauseCarousel);
      this.track?.removeEventListener('mouseleave', this.resumeCarousel);
      this.track?.removeEventListener('focusin', this.pauseCarousel);
      this.track?.removeEventListener('focusout', this.resumeCarousel);
      document.removeEventListener('visibilitychange', this.handleVisibility);
    }
    if (this.handleResize) window.removeEventListener('resize', this.handleResize);
    document.removeEventListener('keydown', this.handleKeydown);
    document.documentElement.classList.remove('melaxin-reel-open');
  }
}

if (!customElements.get('melaxin-video-carousel')) {
  customElements.define('melaxin-video-carousel', MelaxinVideoCarousel);
}
